import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import StatusChip, { STATUS_LABEL } from '../components/StatusChip';
import WarningChips from '../components/WarningChips';
import ClaimDrawer from '../components/ClaimDrawer';
import MapProviderDialog from '../components/MapProviderDialog';
import WorkerStatusChip from '../components/WorkerStatusChip';
import { usePracticeClaims } from './Claims/usePracticeClaims';
import { billingApi } from '../services/billingApi';
import { PracticeContext } from '../contexts/PracticeContext';
import { useAllowedSources } from '../hooks/useAllowedSources';
import type { BillingSource, ClaimStatus, ClaimSummaryDto, UploadSummaryDto } from '../types/billing';

const SOURCE_DISPLAY_NAME: Record<BillingSource, string> = {
  elation: 'Elation',
  athena: 'athenahealth',
};

const SNACKBAR_DURATION_MS = 5000;
const SKELETON_ROW_COUNT = 5;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatCharges(amount: number | null): string {
  if (amount === null) return '-';
  return currencyFormatter.format(amount);
}

// Claims uses last name only (with first as fallback for missing-last cases).
// Triage uses first + last. Intentional UX divergence - keep them separate.
function formatPatientLastNamePreferred(claim: ClaimSummaryDto): string {
  const last = claim.patientLastName?.trim() ?? '';
  if (last.length > 0) return last;
  const first = claim.patientFirstName?.trim() ?? '';
  if (first.length > 0) return first;
  return '-';
}

type SelectionShape = 'empty' | 'staged-only' | 'failed-only' | 'mixed';

function classifySelection(selectedClaims: ClaimSummaryDto[]): SelectionShape {
  if (selectedClaims.length === 0) return 'empty';
  const statuses = new Set(selectedClaims.map((c) => c.status));
  if (statuses.size === 1 && statuses.has('STAGED')) return 'staged-only';
  if (statuses.size === 1 && statuses.has('FAILED')) return 'failed-only';
  return 'mixed';
}

interface SnackbarState {
  open: boolean;
  severity: 'success' | 'error';
  headline: string;
  detail?: string[];
}

const STATUS_OPTIONS: readonly (ClaimStatus | 'ALL')[] = ['STAGED', 'DISPATCHED', 'OA_DRAFT_CREATED', 'SUBMITTED', 'FAILED', 'NEEDS_REVIEW', 'SKIPPED', 'ALL'];

const Claims: React.FC = () => {
  const ctx = useContext(PracticeContext);
  const currentPracticeId = ctx?.currentPracticeId ?? null;

  // Department-derived source filter (Task 10). Filtering is server-side -
  // when the caller is in a single-source department the backend already
  // strips out the other source. We render a quiet caption so the operator
  // knows why their list is narrower than the practice's full claim set.
  const { allowedSources } = useAllowedSources();
  const restrictedToSource: BillingSource | null =
    allowedSources?.length === 1 ? allowedSources[0] : null;

  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status') ?? 'STAGED';
  const uploadIdParam = searchParams.get('uploadId') ?? undefined;
  const fromParam = searchParams.get('from') ?? undefined;
  const toParam = searchParams.get('to') ?? undefined;
  const dirParam: 'asc' | 'desc' = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';

  const [uploads, setUploads] = useState<UploadSummaryDto[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);
  const [mapRow, setMapRow] = useState<ClaimSummaryDto | null>(null);
  const [isActioning, setIsActioning] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    severity: 'success',
    headline: '',
  });

  // A practice switch invalidates any selection made under the previous
  // practice - the ids would silently no-op (server skips wrong_practice)
  // but the selection count would lie.
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPracticeId]);

  // Load upload list for filter autocomplete
  useEffect(() => {
    if (!currentPracticeId) return;
    let cancelled = false;
    billingApi.listUploads(currentPracticeId).then((list) => {
      if (!cancelled) setUploads(list);
    }).catch(() => {
      // Non-critical; autocomplete just stays empty
    });
    return () => {
      cancelled = true;
    };
  }, [currentPracticeId]);

  const filters = useMemo(() => ({
    status: statusParam === 'ALL' ? undefined : [statusParam],
    uploadId: uploadIdParam,
    from: fromParam,
    to: toParam,
    dir: dirParam,
  }), [statusParam, uploadIdParam, fromParam, toParam, dirParam]);

  const { items, workerStatus, nextCursor, isLoading, isLoadingMore, error, reload, loadMore, markActionTaken } =
    usePracticeClaims(currentPracticeId ?? undefined, filters);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedClaims = useMemo(
    () => items.filter((c) => selectedSet.has(c.id)),
    [items, selectedSet],
  );
  const shape = classifySelection(selectedClaims);

  // `pastHeadline` is the success snackbar prefix ("Sent 3 claims to BETHY").
  // `errorVerb` is the infinitive used in the failure message
  // ("Failed to send to BETHY: …"). Splitting them avoids the awkward
  // "${verb.toLowerCase()}" trick that breaks when the user-facing copy
  // is multi-word.
  const ACTIONS = {
    'staged-only': {
      label: 'Send to BETHY',
      pastHeadline: 'Sent',
      pastSuffix: ' to BETHY',
      errorVerb: 'send to BETHY',
      force: false,
    },
    'failed-only': {
      label: 'Retry',
      pastHeadline: 'Retried',
      pastSuffix: '',
      errorVerb: 'retry',
      force: true,
    },
  } as const;

  const action = shape === 'staged-only' || shape === 'failed-only' ? ACTIONS[shape] : null;

  const bulkLabel = action?.label ?? 'Send to BETHY';
  const bulkDisabled = !action || isActioning;

  const bulkTooltip =
    shape === 'empty'
      ? 'Select one or more claims to send or retry'
      : shape === 'mixed'
      ? 'Selection must contain only imported or only failed claims'
      : '';

  const toggleRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((c) => c.id));
    }
  };

  const handleBulkAction = async () => {
    if (!currentPracticeId || !action) return;
    const { force, pastHeadline, pastSuffix, errorVerb } = action;
    setIsActioning(true);
    try {
      const result = await billingApi.dispatch({
        practiceId: currentPracticeId,
        claimIds: selectedIds,
        force,
      });
      const dispatchedCount = result.dispatched.length;
      const skippedCount = result.skipped.length;
      const noMapCount = result.skipped.filter((s) => s.reason === 'no_provider_crosswalk').length;
      const plural = dispatchedCount === 1 ? '' : 's';
      // Call out unmapped-provider skips explicitly; fall back to the generic
      // skipped count for any other skip reasons so nothing is double-counted.
      const otherSkipped = skippedCount - noMapCount;
      const skippedParts: string[] = [];
      if (noMapCount > 0) skippedParts.push(`${noMapCount} skipped (no provider mapping)`);
      if (otherSkipped > 0) skippedParts.push(`${otherSkipped} other skipped`);
      const skippedSuffix = skippedParts.length > 0 ? ` · ${skippedParts.join(', ')}` : '';
      const headline = `${pastHeadline} ${dispatchedCount} claim${plural}${pastSuffix}${skippedSuffix}`;
      const detail =
        skippedCount > 0
          ? result.skipped.map((s) => `${s.claimId.slice(0, 8)}: ${s.reason}`)
          : undefined;
      markActionTaken();
      setSnackbar({ open: true, severity: 'success', headline, detail });
      setSelectedIds([]);
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSnackbar({
        open: true,
        severity: 'error',
        headline: `Failed to ${errorVerb}: ${message}`,
      });
    } finally {
      setIsActioning(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const setParam = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === null) return; // don't allow deselecting all
    setParam('status', value);
    setSelectedIds([]);
  };

  const handleUploadChange = (_: React.SyntheticEvent, value: UploadSummaryDto | null) => {
    setParam('uploadId', value?.id ?? null);
    setSelectedIds([]);
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParam('from', e.target.value || null);
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParam('to', e.target.value || null);
  };

  const selectedUpload = uploads.find((u) => u.id === uploadIdParam) ?? null;

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5">Claims</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading…' : error !== null ? '-' : `${items.length} claims`}
          </Typography>
          {restrictedToSource !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="source-restriction-note"
            >
              Showing {SOURCE_DISPLAY_NAME[restrictedToSource]} claims only - your department's source.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isLoading && error === null && (
            <Typography variant="body2" color="text.secondary">
              {selectedIds.length} of {items.length} selected
            </Typography>
          )}
          <Tooltip title={bulkTooltip} disableHoverListener={!bulkDisabled}>
            <span>
              <Button
                variant="contained"
                disabled={bulkDisabled}
                onClick={handleBulkAction}
              >
                {bulkLabel}
              </Button>
            </span>
          </Tooltip>
          <IconButton aria-label="Refresh" onClick={reload} size="small">
            <RefreshIcon />
          </IconButton>
          {workerStatus && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <WorkerStatusChip status={workerStatus} />
            </>
          )}
        </Box>
      </Box>

      {/* Toolbar: status filter + upload filter + date range */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={statusParam}
          exclusive
          onChange={handleStatusChange}
          size="small"
          aria-label="status filter"
        >
          {STATUS_OPTIONS.map((s) => (
            <ToggleButton key={s} value={s} aria-label={STATUS_LABEL[s as ClaimStatus] ?? 'All'}>
              {STATUS_LABEL[s as ClaimStatus] ?? 'All'}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Autocomplete
          options={uploads}
          value={selectedUpload}
          onChange={handleUploadChange}
          getOptionLabel={(opt) => opt.filename}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField {...params} label="Upload" size="small" sx={{ minWidth: 200 }} />
          )}
          size="small"
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={fromParam ?? ''}
          onChange={handleFromChange}
        />

        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={toParam ?? ''}
          onChange={handleToChange}
        />
      </Box>

      {/* Loading skeleton */}
      {isLoading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Skeleton variant="rectangular" width={20} height={20} animation="wave" />
                </TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={20} height={20} animation="wave" />
                  </TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Error state */}
      {error !== null && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load claims</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && error === null && items.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <InboxOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No claims found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Adjust the filters above or upload a new CSV from the Upload page.
          </Typography>
        </Paper>
      )}

      {/* Claims table */}
      {!isLoading && error === null && items.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'select all claims' }}
                  />
                </TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const isSelected = selectedSet.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpenClaimId(row.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ 'aria-label': `select claim ${row.id}` }}
                      />
                    </TableCell>
                    <TableCell>{formatPatientLastNamePreferred(row)}</TableCell>
                    <TableCell>{row.dateOfService ?? '-'}</TableCell>
                    <TableCell>{row.primaryCarrier ?? '-'}</TableCell>
                    <TableCell>{row.lineCount ?? '-'}</TableCell>
                    <TableCell>{formatCharges(row.totalCharges)}</TableCell>
                    <TableCell>
                      {row.providerMapped === false && row.status === 'STAGED' ? (
                        <Chip
                          color="warning"
                          size="small"
                          label="No provider mapping"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMapRow(row);
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} queuePosition={row.queuePosition} />
                    </TableCell>
                    <TableCell>
                      <WarningChips codes={row.warnings} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Load more */}
      {!isLoading && error === null && nextCursor !== null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => void loadMore()}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      <ClaimDrawer claimId={openClaimId} onClose={() => setOpenClaimId(null)} />

      {mapRow !== null && (
        <MapProviderDialog
          open
          practiceId={mapRow.practiceId}
          providerName={mapRow.providerName ?? ''}
          supervisingProviderName={mapRow.supervisingProviderName ?? null}
          onClose={() => setMapRow(null)}
          onMapped={() => {
            setMapRow(null);
            reload();
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={SNACKBAR_DURATION_MS}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.headline}
          {snackbar.detail && snackbar.detail.length > 0 && (
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {snackbar.detail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Claims;
